import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { Release, Tracklist } from "../api/types";
import { Modal, fmtDur } from "./Modal";

/**
 * The tracklist: number, title, length, and what the audio pass read off it --
 * tempo, key and meter, where they are known.
 *
 * The BPM cell is the point of this popup. A track's tempo is one of three
 * things and they are not the same thing said differently:
 *
 *   a number   measured from the audio
 *   unknown    looked at, and there is nothing to find
 *   —          nobody has looked yet
 *
 * Bandcamp reports a length of zero for tracks it will not stream -- pre-orders
 * and partial streams -- so those are left blank and the running time is marked
 * "(partial)" rather than pretending to be complete.
 *
 * A release that is not on Bandcamp has no tracklist of Bandcamp's, but a shop
 * that plays clips for it has a list of those, and that is what shows -- with
 * `source` saying whose it is. Nothing has measured a shop's clip yet, so the
 * tempo, key and meter columns are honest dashes.
 */
export function TracksModal({ release, onClose }: { release: Release; onClose: () => void }) {
  const [list, setList] = useState<Tracklist | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.tracks(release.key)
      .then((t) => alive && setList(t))
      .catch((e: Error) => alive && setError(e.message));
    return () => { alive = false; };
  }, [release.key]);

  const known = list?.tracks.filter((t) => t.bpm.state === "measured").length ?? 0;
  const keyed = list?.tracks.filter((t) => !!t.key).length ?? 0;

  return (
    <Modal
      title={release.title}
      who={release.artist}
      onClose={onClose}
      meta={
        list && (
          <>
            {list.source && <>clips from {list.source.url
              ? <a href={list.source.url} target="_blank" rel="noopener noreferrer">{list.source.label}</a>
              : list.source.label} · </>}
            {list.count} {list.source ? "clip" : "track"}{list.count === 1 ? "" : "s"}
            {list.duration > 0 && <> · {fmtDur(list.duration)}</>}
            {list.partial && " (partial)"}
            {known > 0 && ` · ${known} with a BPM`}
            {keyed > 0 && ` · ${keyed} keyed`}
          </>
        )
      }
    >
      {error && <div className="note">{error}</div>}
      {!list && !error && <div className="note">loading…</div>}
      {list && (
        <>
          <div className="tbl"><table>
            <tbody>
              {list.tracks.map((t) => (
                <tr key={t.pos}>
                  <td className="n">{t.label ?? t.n ?? t.pos}</td>
                  <td>{t.title}</td>
                  <td className="d">{t.dur ? fmtDur(t.dur) : ""}</td>
                  <td className={`b${t.bpm.state === "measured" ? "" : " none"}`}
                      title={bpmTitle(t.bpm)}>
                    {t.bpm.state === "measured"
                      ? `${t.bpm.value} BPM`
                      : t.bpm.state === "unknown"
                        ? "unknown"
                        : t.bpm.state === "unavailable"
                          ? "no audio"
                          : "—"}
                  </td>
                  {/* Read from the same pass as the tempo, so they appear and
                      vanish together. A dash rather than a blank: the column
                      is empty because nobody has listened yet, which is worth
                      showing rather than hiding. */}
                  <td className={`k${t.key ? "" : " none"}`}
                      title={t.keyConf ? `confidence ${t.keyConf}` : "not read yet"}>
                    {t.key ?? "—"}
                  </td>
                  <td className={`m${t.meter ? "" : " none"}`}
                      title={t.meterConf ? `confidence ${t.meterConf}` : "not read yet"}>
                    {t.meter ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
          {!list.source && list.tracks.some((t) => t.bpm.state !== "measured") && (
            <div className="note">
              A tempo is one of three things: a number was measured from the audio;
              <b> unknown</b> means it was listened to and no steady pulse was found;
              <b> —</b> means nobody has listened yet; <b>no audio</b> means Bandcamp
              streams none of it.
            </div>
          )}
          {list.partial && !list.source && (
            <div className="note">
              Bandcamp will not stream every track here — a pre-order or a partial
              stream comes back with no length and no audio — so the running time
              is of the tracks it does serve.
            </div>
          )}
          {list.source && (
            <div className="note">
              Not a Bandcamp tracklist: these are the clips {list.source.label} plays
              on its own page, in its own order, with lengths only where the shop
              states them.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function bpmTitle(b: Tracklist["tracks"][number]["bpm"]): string {
  if (b.state === "measured") {
    return b.conf ? `measured from the audio, confidence ${b.conf}` : "measured from the audio";
  }
  if (b.state === "unchecked") return "nobody has looked at this track yet";
  if (b.state === "unavailable") return "Bandcamp streams no audio for this track, so nothing was heard";
  // An "unknown" also records how deep the search behind it went, which is what
  // decides whether it is the last word.
  const heard = b.windows ? `${b.windows} window${b.windows === 1 ? "" : "s"} heard` : "listened";
  const final = b.ranOut
    ? "the audio ran out, so this is final"
    : "the search was capped, so a longer run could still find one";
  const best = b.best ? `; closest reading ${b.best}` : "";
  return `${heard}, no steady pulse — ${final}${best}`;
}
